import {
  Card,
  InfiniteLoader,
  ManagementListToolbar,
  StatusPill,
} from "@taunggyi-plus/design-system";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { useAuth } from "@taunggyi-plus/auth";
import type { Locale } from "@taunggyi-plus/domain";
import { CustomerAuthDialog, CustomerHotelBooking } from "@taunggyi-plus/hotel-feature";
import { CustomerDeliveryDirectory } from "@taunggyi-plus/delivery-feature";
import { CustomerMerchantOrdering } from "@taunggyi-plus/merchant-feature";
import { CustomerTaxiDirectory } from "@taunggyi-plus/taxi-feature";

type LiveServiceId = "hotel" | "food" | "delivery" | "taxi";
type ServiceId = LiveServiceId | "wash" | "night";

interface CustomerService {
  readonly id: ServiceId;
  readonly title: string;
  readonly description: string;
  readonly comingSoon?: boolean;
}

const copy = {
  en: {
    search: "Search all services",
    searchPlaceholder: "Search TaungGyi Link services",
    hotel: "Link Stay",
    food: "Link Food",
    delivery: "Link Delivery",
    taxi: "Link Ride",
    wash: "Link Wash",
    night: "Link Night",
    hotelDescription: "Find rooms, check details, and request a booking.",
    foodDescription: "Browse local restaurants and order food for cash on delivery.",
    deliveryDescription: "Request delivery from participating local shops.",
    taxiDescription: "Find a verified driver and call inside the app.",
    washDescription: "Laundry pickup and care from trusted local providers.",
    nightDescription: "Discover nightlife, bars, and table booking in Taunggyi.",
    comingSoon: "Coming soon",
    noResults: "No services match your search.",
    details: "View details",
    close: "Close service details",
    recent: "Recent activities",
    recentEmpty: "Your latest bookings, orders, and calls will appear here.",
    popular: "Popular in Taunggyi",
    promotions: "Special promotions",
    promotionTitle: "Opening specials are coming soon",
    promotionDescription: "Discover offers from verified Taunggyi partners in one place.",
    explore: "Explore services",
    requestDelivery: "Request delivery",
    showMore: "Show more",
    showLess: "Show less",
    seeMore: "See more",
    popularHelp: "Trusted providers customers are exploring now.",
    promotionAction: "View offer",
  },
  my: {
    search: "ဝန်ဆောင်မှုအားလုံး ရှာရန်",
    searchPlaceholder: "TaungGyi Link ဝန်ဆောင်မှုများ ရှာပါ",
    hotel: "Link Stay",
    food: "Link Food",
    delivery: "Link Delivery",
    taxi: "Link Ride",
    wash: "Link Wash",
    night: "Link Night",
    hotelDescription: "အခန်းများရှာ၊ အသေးစိတ်ကြည့်ပြီး ဘွတ်ကင်တောင်းဆိုပါ။",
    foodDescription: "ဒေသစားသောက်ဆိုင်များမှ မှာယူပြီး ပစ္စည်းရောက်ငွေချေပါ။",
    deliveryDescription: "ပါဝင်သော ဒေသဆိုင်များမှ ပို့ဆောင်မှု တောင်းဆိုပါ။",
    taxiDescription: "အတည်ပြုတက္ကစီအဖွဲ့များကြည့်ပြီး အော်ပရေတာကို တိုက်ရိုက်ခေါ်ပါ။",
    washDescription: "ယုံကြည်ရသော ဒေသခံဝန်ဆောင်မှုများထံမှ အဝတ်လျှော်ယူပို့ ဝန်ဆောင်မှု။",
    nightDescription: "တောင်ကြီးရှိ ညဘက်လည်ပတ်စရာနှင့် စားပွဲကြိုတင်မှာယူမှုများ။",
    comingSoon: "မကြာမီလာမည်",
    noResults: "သင့်ရှာဖွေမှုနှင့် ကိုက်ညီသော ဝန်ဆောင်မှုမရှိပါ။",
    details: "အသေးစိတ်ကြည့်ရန်",
    close: "ဝန်ဆောင်မှုအသေးစိတ် ပိတ်မည်",
    recent: "လတ်တလောလုပ်ဆောင်မှုများ",
    recentEmpty: "သင့်ဘွတ်ကင်၊ မှာယူမှုနှင့် ခေါ်ဆိုမှုများကို ဤနေရာတွင် ပြပါမည်။",
    popular: "တောင်ကြီးတွင် လူကြိုက်များသော ဝန်ဆောင်မှုများ",
    promotions: "အထူးအစီအစဉ်များ",
    promotionTitle: "ဖွင့်ပွဲအထူးအစီအစဉ်များ မကြာမီလာမည်",
    promotionDescription:
      "တောင်ကြီးရှိ အတည်ပြုပါတနာများ၏ ကမ်းလှမ်းချက်များကို တစ်နေရာတည်းတွင် ရှာပါ။",
    explore: "ဝန်ဆောင်မှုများ ရှာဖွေပါ",
    requestDelivery: "ပို့ဆောင်မှု တောင်းဆိုမည်",
    showMore: "နောက်ထပ်ကြည့်ရန်",
    showLess: "အနည်းငယ်သာကြည့်ရန်",
    seeMore: "အားလုံးကြည့်ရန်",
    popularHelp: "ဖောက်သည်များ ယခုရှာဖွေနေသော ယုံကြည်ရသည့် ဝန်ဆောင်မှုပေးသူများ။",
    promotionAction: "ကမ်းလှမ်းချက်ကြည့်ရန်",
  },
  shn: {
    search: "ႁႃဝန်ဆောင်ႈတင်းသဵင်ႈ",
    searchPlaceholder: "ႁႃဝန်ဆောင်ႈ TaungGyi Link",
    hotel: "Link Stay",
    food: "Link Food",
    delivery: "Link Delivery",
    taxi: "Link Ride",
    wash: "Link Wash",
    night: "Link Night",
    hotelDescription: "ႁႃႁွင်ႈ၊ တူၺ်းၶေႃႈမုၼ်းလႄႈသူင်ႇၶေႃႈယွၼ်းၸွင်း။",
    foodDescription: "လိူၵ်ႈႁၢၼ်ႉတၢင်းၵိၼ်လႄႈသင်ႇသိုဝ်ႉငိုၼ်းၼႂ်းမိုဝ်း။",
    deliveryDescription: "သူင်ႇၶေႃႈယွၼ်းသူင်ႇၶူဝ်းတီႈႁၢၼ်ႉၼႂ်းပိုၼ်ႉတီႈ။",
    taxiDescription: "တူၺ်းၸုမ်းတႅၵ်ႇသီႇဢၼ်ယိုၼ်ယၼ်လႄႈႁွင်ႉ operator။",
    washDescription: "ဝန်ဆောင်ႈသၵ်ႉၶူဝ်းလႄႈႁပ်ႉသူင်ႇၼႂ်းပိုၼ်ႉတီႈ။",
    nightDescription: "ႁႃတီႈလႄႇၵၢင်ၶမ်ႈလႄႈၸွင်းၶူင်ႇၼင်ႈၼႂ်းတူၼ်ႈတီး။",
    comingSoon: "တေမႃးဝႆးဝႆးၼႆႉ",
    noResults: "ဢမ်ႇမီးဝန်ဆောင်ႈဢၼ်ၶဝ်ႈၵၼ်တင်းၶေႃႈႁႃ။",
    details: "တူၺ်းၶေႃႈမုၼ်း",
    close: "ပိၵ်ႉၶေႃႈမုၼ်းဝန်ဆောင်ႈ",
    recent: "ၵၢၼ်ႁဵတ်းလႃႈသုတ်း",
    recentEmpty: "ၵၢၼ်ၸွင်း၊ သင်ႇသိုဝ်ႉလႄႈႁွင်ႉၾူၼ်းတေဢွၵ်ႇတီႈၼႆႈ။",
    popular: "ဝန်ဆောင်ႈဢၼ်ၵူၼ်းသူင်ၼႂ်းတူၼ်ႈတီး",
    promotions: "ၶေႃႈလူတ်းယွမ်းပိူင်ၶိုၵ်ႉ",
    promotionTitle: "ၶေႃႈလူတ်းယွမ်းပိုတ်ႇမႂ်ႇ တေမႃးဝႆးဝႆးၼႆႉ",
    promotionDescription: "ႁႃၶေႃႈသၢင်ႈပၼ်ၶွင် partner တူၼ်ႈတီးဢၼ်ယိုၼ်ယၼ်ဝႆႉတီႈလဵဝ်။",
    explore: "ႁႃဝန်ဆောင်ႈ",
    requestDelivery: "ယွၼ်းသူင်ႇၶူဝ်း",
    showMore: "တူၺ်းထႅင်ႈ",
    showLess: "တူၺ်းဢေႇ",
    seeMore: "တူၺ်းတင်းမူတ်း",
    popularHelp: "ၽူႈပၼ်ဝန်ဆောင်ႈဢၼ်ၽူႈၸႂ်ႉတိုၵ်ႉႁႃ။",
    promotionAction: "တူၺ်းၶေႃႈလူတ်းယွမ်း",
  },
  zh: {
    search: "搜索全部服务",
    searchPlaceholder: "搜索 TaungGyi Link 服务",
    hotel: "Link Stay",
    food: "Link Food",
    delivery: "Link Delivery",
    taxi: "Link Ride",
    wash: "Link Wash",
    night: "Link Night",
    hotelDescription: "查找客房、查看详情并提交预订申请。",
    foodDescription: "浏览本地餐厅，选择货到付款订餐。",
    deliveryDescription: "向参与服务的本地商店申请配送。",
    taxiDescription: "查看认证出租车组并直接致电调度员。",
    washDescription: "由可信赖的本地服务商提供洗衣取送服务。",
    nightDescription: "探索东枝夜生活、酒吧与订桌服务。",
    comingSoon: "即将推出",
    noResults: "没有符合搜索条件的服务。",
    details: "查看详情",
    close: "关闭服务详情",
    recent: "最近活动",
    recentEmpty: "您最近的预订、订单和通话将显示在这里。",
    popular: "东枝热门服务",
    promotions: "特别优惠",
    promotionTitle: "开业特别优惠即将推出",
    promotionDescription: "在一个地方发现东枝认证合作伙伴的优惠。",
    explore: "探索服务",
    requestDelivery: "申请配送",
    showMore: "查看更多",
    showLess: "收起",
    seeMore: "查看全部",
    popularHelp: "客户近期正在浏览的可信服务商。",
    promotionAction: "查看优惠",
  },
} as const;

interface PublicPromotion {
  placement: string;
  id: string;
  provider_kind: "hotel" | "merchant" | "delivery" | "taxi";
  resource_name: string;
  title: string;
  description: string;
  terms: string;
  starts_on: string;
  ends_on: string;
  display_priority: number;
}

function CustomerPromotions({
  locale,
  limit,
  service,
  hero = false,
}: {
  readonly locale: Locale;
  readonly limit?: number;
  readonly service?: LiveServiceId;
  readonly hero?: boolean;
}) {
  const auth = useAuth();
  const text = copy[locale];
  const [promotions, setPromotions] = useState<PublicPromotion[]>([]);
  const providerKind = service === "food" ? "merchant" : service;

  useEffect(() => {
    if (!auth.client || !auth.user) return;
    let active = true;
    void auth.client.rpc("published_promotions").then(({ data, error }) => {
      if (!active) return;
      const rows = error
        ? []
        : ((data ?? []) as PublicPromotion[]).filter((p) =>
            providerKind
              ? p.provider_kind === providerKind && p.placement === "service"
              : p.placement === "home",
          );
      setPromotions(limit ? rows.slice(0, limit) : rows);
    });
    return () => {
      active = false;
    };
  }, [auth.client, auth.user, limit, providerKind]);

  const promotionId = new URLSearchParams(window.location.search).get("promotion");
  const chosen = promotions.find((p) => p.id === promotionId);
  if (chosen)
    return (
      <article className="record-detail-page">
        <a href="/services?filter=promotions" className="more-back-link">
          ← Promotions
        </a>
        <Card>
          <p className="eyebrow">{chosen.resource_name}</p>
          <h2>{chosen.title}</h2>
          <p>{chosen.description}</p>
          <h3>Offer terms</h3>
          <p>{chosen.terms}</p>
          <p>
            {chosen.starts_on} – {chosen.ends_on}
          </p>
          <a
            className="button"
            href={`/services?type=${chosen.provider_kind === "merchant" ? "food" : chosen.provider_kind}`}
          >
            Explore this service
          </a>
        </Card>
      </article>
    );
  if (hero && !promotions.length) return null;
  if (!promotions.length)
    return (
      <div className="promotion-grid">
        <div className="promotion-card promotion-card--empty">
          <span className="promotion-card__badge">★</span>
          <span>
            <strong>{text.promotionTitle}</strong>
            <small>{text.promotionDescription}</small>
          </span>
        </div>
      </div>
    );

  return (
    <div className="promotion-grid">
      {promotions.map((promotion, index) => {
        return (
          <a
            className="promotion-card"
            href={`/services?filter=promotions&promotion=${promotion.id}`}
            key={promotion.id}
            onClick={() => {
              void auth.client?.rpc("record_ad_engagement", {
                promotion: promotion.id,
                kind: "click",
              });
            }}
            ref={(node) => {
              if (!node) return;
              const observer = new IntersectionObserver(
                (entries) => {
                  if (entries.some((e) => e.isIntersecting)) {
                    void auth.client?.rpc("record_ad_engagement", {
                      promotion: promotion.id,
                      kind: "view",
                    });
                    observer.disconnect();
                  }
                },
                { threshold: 0.5 },
              );
              observer.observe(node);
              return () => observer.disconnect();
            }}
          >
            <span className="promotion-card__badge">{index + 1}</span>
            <span>
              <small>{promotion.resource_name}</small>
              <strong>{promotion.title}</strong>
              <small>{promotion.description}</small>
              <em>{text.promotionAction}</em>
            </span>
            <b aria-hidden="true">→</b>
          </a>
        );
      })}
    </div>
  );
}

export function CustomerHome({ locale }: { readonly locale: Locale }) {
  const text = copy[locale];
  const [query, setQuery] = useState("");
  const [searchResultCount, setSearchResultCount] = useState(0);
  const services = useMemo<readonly CustomerService[]>(
    () => [
      { id: "hotel" as const, title: text.hotel, description: text.hotelDescription },
      { id: "food" as const, title: text.food, description: text.foodDescription },
      { id: "delivery" as const, title: text.delivery, description: text.deliveryDescription },
      { id: "taxi" as const, title: text.taxi, description: text.taxiDescription },
      {
        id: "wash" as const,
        title: text.wash,
        description: text.washDescription,
        comingSoon: true,
      },
      {
        id: "night" as const,
        title: text.night,
        description: text.nightDescription,
        comingSoon: true,
      },
    ],
    [text],
  );
  const normalizedQuery = query.trim();

  return (
    <div className="customer-home">
      <div className="home-hero-promotion">
        <CustomerPromotions locale={locale} limit={1} hero />
      </div>
      <section className="home-section" aria-labelledby="home-search-title">
        <div className="home-section__heading">
          <h2 id="home-search-title">{text.search}</h2>
          {normalizedQuery && (
            <a className="home-show-more" href={`/search?q=${encodeURIComponent(normalizedQuery)}`}>
              {text.seeMore}
            </a>
          )}
        </div>
        <form
          className="home-search"
          action="/search"
          onSubmit={(event) => {
            event.preventDefault();
            const nextLocation = `/search${normalizedQuery ? `?q=${encodeURIComponent(normalizedQuery)}` : ""}`;
            window.history.pushState({}, "", nextLocation);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }}
        >
          <span className="skip-link">{text.search}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m16 16 5 5" />
          </svg>
          <input
            id="home-service-search"
            name="q"
            type="search"
            value={query}
            placeholder={text.searchPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
          />
        </form>
        {normalizedQuery && (
          <div className="home-result-grid" aria-live="polite">
            <UnifiedDirectorySearch
              locale={locale}
              query={query}
              noResults={text.noResults}
              onResultCount={setSearchResultCount}
              preview
            />
            <span className="home-result-count">{searchResultCount}</span>
          </div>
        )}
      </section>

      <section className="home-section" aria-labelledby="home-categories-title">
        <div className="home-section__heading">
          <h2 id="home-categories-title">{text.explore}</h2>
        </div>
        <div className="home-category-grid">
          {services.map((service) => (
            <a className="home-category" href={`/services?type=${service.id}`} key={service.id}>
              <ServiceArtwork service={service.id} compact />
              <strong>{service.title}</strong>
              {service.comingSoon && <span className="service-coming-soon">{text.comingSoon}</span>}
            </a>
          ))}
        </div>
      </section>

      <section className="home-section" aria-labelledby="home-promotions-title">
        <div className="home-section__heading">
          <h2 id="home-promotions-title">{text.promotions}</h2>
          <a className="home-show-more" href="/services?filter=promotions">
            {text.seeMore}
          </a>
        </div>
        <CustomerPromotions locale={locale} limit={5} />
      </section>

      <section className="home-section" aria-labelledby="home-popular-title">
        <div className="home-section__heading">
          <div className="home-section__intro">
            <h2 id="home-popular-title">{text.popular}</h2>
          </div>
          <a className="home-show-more" href="/services?filter=popular">
            {text.seeMore}
          </a>
        </div>
        <DiscoveryList locale={locale} sort="popular" preview />
      </section>
      <section className="home-section">
        <div className="home-section__heading">
          <h2>Recently added</h2>
          <a href="/services">{text.seeMore}</a>
        </div>
        <DiscoveryList locale={locale} preview />
      </section>
    </div>
  );
}

export function CustomerSearch({ locale }: { readonly locale: Locale }) {
  const text = copy[locale];
  const [query, setQuery] = useState(
    () => new URLSearchParams(window.location.search).get("q") ?? "",
  );
  const [resultCount, setResultCount] = useState(0);
  const normalizedQuery = query.trim();

  return (
    <div className="customer-search-page">
      <label className="home-search" htmlFor="customer-search-page-input">
        <span className="skip-link">{text.search}</span>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m16 16 5 5" />
        </svg>
        <input
          id="customer-search-page-input"
          type="search"
          value={query}
          placeholder={text.searchPlaceholder}
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      {normalizedQuery && (
        <section className="home-section" aria-label={text.search}>
          <div className="home-section__heading">
            <span>{resultCount}</span>
          </div>
          <div className="home-result-grid" aria-live="polite">
            <UnifiedDirectorySearch
              locale={locale}
              query={query}
              noResults={text.noResults}
              onResultCount={setResultCount}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function UnifiedDirectorySearch({
  locale,
  query,
  noResults,
  onResultCount,
  preview = false,
}: {
  readonly locale: Locale;
  readonly query: string;
  readonly noResults: string;
  readonly onResultCount?: (count: number) => void;
  readonly preview?: boolean;
}) {
  const [counts, setCounts] = useState<Record<LiveServiceId, number>>({
    hotel: 0,
    food: 0,
    delivery: 0,
    taxi: 0,
  });
  const setHotelCount = useCallback(
    (count: number) =>
      setCounts((current) => (current.hotel === count ? current : { ...current, hotel: count })),
    [],
  );
  const setFoodCount = useCallback(
    (count: number) =>
      setCounts((current) => (current.food === count ? current : { ...current, food: count })),
    [],
  );
  const setDeliveryCount = useCallback(
    (count: number) =>
      setCounts((current) =>
        current.delivery === count ? current : { ...current, delivery: count },
      ),
    [],
  );
  const setTaxiCount = useCallback(
    (count: number) =>
      setCounts((current) => (current.taxi === count ? current : { ...current, taxi: count })),
    [],
  );
  const total = counts.hotel + counts.food + counts.delivery + counts.taxi;
  useEffect(() => onResultCount?.(total), [onResultCount, total]);
  const renderAuthPrompt = (onClose: () => void) => (
    <CustomerAuthDialog locale={locale} onClose={onClose} />
  );

  return (
    <>
      <div className="unified-search-results">
        <CustomerHotelBooking
          locale={locale}
          variant="home"
          searchQuery={query}
          limit={preview ? 2 : undefined}
          onResultCount={setHotelCount}
        />
        <CustomerMerchantOrdering
          locale={locale}
          variant="home"
          searchQuery={query}
          limit={preview ? 1 : undefined}
          onResultCount={setFoodCount}
          renderAuthPrompt={renderAuthPrompt}
        />
        <CustomerDeliveryDirectory
          locale={locale}
          variant="home"
          searchQuery={query}
          limit={preview ? 1 : undefined}
          onResultCount={setDeliveryCount}
          renderAuthPrompt={renderAuthPrompt}
        />
        <CustomerTaxiDirectory
          locale={locale}
          variant="home"
          searchQuery={query}
          limit={preview ? 1 : undefined}
          onResultCount={setTaxiCount}
          renderAuthPrompt={renderAuthPrompt}
        />
      </div>
      {total === 0 && <div className="service-no-results">{noResults}</div>}
    </>
  );
}

function CategoryDirectory({
  category,
  locale,
  limit,
}: {
  readonly category: LiveServiceId;
  readonly locale: Locale;
  readonly limit?: number;
}) {
  const renderAuthPrompt = (onClose: () => void) => (
    <CustomerAuthDialog locale={locale} onClose={onClose} />
  );

  if (category === "hotel")
    return <CustomerHotelBooking locale={locale} variant="home" limit={limit} />;
  if (category === "food")
    return (
      <CustomerMerchantOrdering
        locale={locale}
        variant="home"
        limit={limit}
        renderAuthPrompt={renderAuthPrompt}
      />
    );
  if (category === "delivery")
    return (
      <CustomerDeliveryDirectory
        locale={locale}
        variant="home"
        limit={limit}
        renderAuthPrompt={renderAuthPrompt}
      />
    );
  return (
    <CustomerTaxiDirectory
      locale={locale}
      variant="home"
      limit={limit}
      renderAuthPrompt={renderAuthPrompt}
    />
  );
}

type DiscoveryItem = {
  id: string;
  service: LiveServiceId;
  title: string;
  description: string;
  address: string;
  available: boolean;
  bookings: number;
  photo_data_url?: string | null;
};
function DiscoveryArtwork({ item }: { item: DiscoveryItem }) {
  const [failed, setFailed] = useState(false);
  if (item.service !== "taxi" || !item.photo_data_url || failed)
    return <ServiceArtwork service={item.service} />;
  return (
    <img
      className="discovery-card__driver-photo"
      src={item.photo_data_url}
      alt={`${item.title} portrait`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
function DiscoveryList({
  locale,
  preview,
  sort = "recent",
  initialService = "all",
}: {
  locale: Locale;
  preview?: boolean;
  sort?: string;
  initialService?: string;
}) {
  const { client } = useAuth();
  const [query, setQuery] = useState(""),
    [service, setService] = useState(initialService),
    [term, setTerm] = useState(""),
    [availability, setAvailability] = useState("all"),
    [order, setOrder] = useState(sort),
    [page, setPage] = useState(0);
  const [terms, setTerms] = useState<{ id: string; name: string; kind: string }[]>([]);
  const [state, setState] = useState<{
    key: string;
    page: number;
    items: DiscoveryItem[];
    total: number;
    error: string;
  }>({ key: "", page: -1, items: [], total: 0, error: "" });
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify([query, service, term, availability, order]);
  const loading = state.key !== key || state.page !== page;
  useEffect(() => {
    if (!client) return;
    let active = true;
    void client
      .from("catalog_terms")
      .select("id,name,kind")
      .eq("is_active", true)
      .then((r) => {
        if (active && !r.error) setTerms(r.data as typeof terms);
      });
    return () => {
      active = false;
    };
  }, [client]);
  useEffect(() => {
    if (!client) return;
    let active = true;
    const timer = setTimeout(() => {
      void client
        .rpc("discovery_page", {
          query,
          service,
          term: term || null,
          available_only: availability === "available",
          sort_order: order,
          page,
        })
        .then((r) => {
          if (!active) return;
          const data = r.data as { items: DiscoveryItem[]; total: number } | null;
          setState((old) => ({
            key,
            page,
            total: data?.total ?? 0,
            error: r.error?.message ?? "",
            items:
              page === 0
                ? (data?.items ?? [])
                : [
                    ...old.items,
                    ...(data?.items ?? []).filter((n) => !old.items.some((o) => o.id === n.id)),
                  ],
          }));
        });
    }, 150);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [client, key, page, query, service, term, availability, order, revision]);
  function change(set: (value: string) => void, value: string) {
    set(value);
    setPage(0);
  }
  const items = state.key === key ? state.items : [];
  return (
    <section>
      {!preview && (
        <ManagementListToolbar
          query={query}
          onQueryChange={(v) => change(setQuery, v)}
          searchLabel={copy[locale].search}
          resultCount={state.total}
          filters={[
            {
              label: "Service",
              value: service,
              onChange: (v: string) => change(setService, v),
              options: [
                { value: "all", label: "All services" },
                ...(["hotel", "food", "delivery", "taxi"] as const).map((value) => ({
                  value,
                  label: copy[locale][value],
                })),
              ],
            },
            {
              label: "Category or tag",
              value: term,
              onChange: (v: string) => change(setTerm, v),
              options: [
                { value: "", label: "All categories and tags" },
                ...terms.map((t) => ({ value: t.id, label: t.name })),
              ],
            },
            {
              label: "Availability",
              value: availability,
              onChange: (v: string) => change(setAvailability, v),
              options: [
                { value: "all", label: "All" },
                { value: "available", label: "Accepting requests" },
              ],
            },
            {
              label: "Sort",
              value: order,
              onChange: (v: string) => change(setOrder, v),
              options: [
                { value: "recent", label: "Recently added" },
                { value: "popular", label: "Most booked / ordered" },
              ],
            },
          ]}
          onClear={() => {
            setQuery("");
            setService("all");
            setTerm("");
            setAvailability("all");
            setPage(0);
          }}
        />
      )}
      {state.error && (
        <p role="alert">
          {state.error} <button onClick={() => setRevision((n) => n + 1)}>Retry</button>
        </p>
      )}
      {!loading && !items.length && !state.error && <Card>{copy[locale].noResults}</Card>}
      <div className="discovery-grid">
        {(preview ? items.slice(0, 4) : items).map((item) => (
          <a
            key={item.id}
            className="discovery-card"
            href={`/services?type=${item.service}&provider=${item.id}`}
          >
            <DiscoveryArtwork key={item.photo_data_url ?? item.id} item={item} />
            <span className="discovery-card__body">
              <strong>{item.title}</strong>
              <p>{item.description}</p>
              <StatusPill>{item.available ? "Available" : "Currently unavailable"}</StatusPill>
              <small>
                {order === "popular" ? `${item.bookings} bookings / orders` : item.address}
              </small>
            </span>
          </a>
        ))}
      </div>
      {loading && <p role="status">Loading services…</p>}
      {!preview && (
        <InfiniteLoader
          loading={loading}
          hasMore={!state.error && items.length < state.total}
          onLoad={() => setPage((p) => p + 1)}
          label={`${items.length} of ${state.total}`}
        />
      )}
    </section>
  );
}
export function CustomerServiceBrowser({ locale }: { readonly locale: Locale }) {
  const params = new URLSearchParams(window.location.search);
  const service = params.get("type") ?? "all";
  if (params.has("provider") && ["hotel", "food", "delivery", "taxi"].includes(service))
    return (
      <>
        <a className="more-back-link" href={`/services?type=${service}`}>
          ← {copy[locale].explore}
        </a>
        <CategoryDirectory category={service as LiveServiceId} locale={locale} />
      </>
    );
  if (params.get("filter") === "promotions") return <CustomerPromotions locale={locale} />;
  if (["wash", "night"].includes(service)) return <Card>{copy[locale].comingSoon}</Card>;
  return (
    <DiscoveryList
      locale={locale}
      initialService={service}
      sort={params.get("filter") === "popular" ? "popular" : "recent"}
    />
  );
}

function ServiceArtwork({
  service,
  compact = false,
}: {
  readonly service: ServiceId;
  readonly compact?: boolean;
}) {
  const glyphs: Record<ServiceId, ReactNode> = {
    hotel: (
      <>
        <path d="M5 20V8l7-4 7 4v12" />
        <path d="M9 20v-5h6v5M8 10h1m6 0h1" />
      </>
    ),
    food: (
      <>
        <path d="M7 4v7m-3-7v4a3 3 0 0 0 6 0V4m-3 7v9M16 4v16m0-16c3 2 4 6 0 9" />
      </>
    ),
    delivery: (
      <>
        <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
      </>
    ),
    taxi: (
      <>
        <path d="m5 10 2-5h10l2 5M4 10h16v7H4z" />
        <circle cx="7" cy="17" r="2" />
        <circle cx="17" cy="17" r="2" />
        <path d="M7 13h.01M17 13h.01" />
      </>
    ),
    wash: (
      <>
        <path d="M5 5h14v14H5zM8 5v4h8V5" />
        <circle cx="12" cy="14" r="3.5" />
        <path d="M8 2h.01M11 2h.01" />
      </>
    ),
    night: (
      <>
        <path d="M5 4h14l-5.5 7v7M10 21h7" />
        <path d="M8 7h8" />
      </>
    ),
  };
  return (
    <span
      className={`service-art service-art--${service} ${compact ? "service-art--compact" : ""}`}
      aria-hidden="true"
    >
      <span className="service-art__sun" />
      <svg viewBox="0 0 24 24">{glyphs[service]}</svg>
      <span className="service-art__mountains" />
    </span>
  );
}
